import {
  CatalogPlanSeatResponseDto,
  EventSessionForFilterResponseDto,
  EventSessionPlanCategoryResponseDto,
  EventSessionPlanContingentResponseDto,
  EventSessionPlanUnavailableSeatTypesResponseDto,
  SeatStatusEnumResponseDto,
} from '@core/services/apiclient';
import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';

export enum SelectionType {
  Drag = 'Drag',
  Default = 'Default',
  Lasso = 'Lasso',
}

export interface SeatStatusOption {
  field: SeatStatusEnumResponseDto;
  label: string;
  icon: string;
  iconPosition?: string;
  statusOptionEnable: boolean;
}
export interface LegendSeatsStatus {
  field: SeatStatusEnumResponseDto;
  label: string;
  icon: string;
  iconPosition?: string;
}
export interface SeatStatusFilterItem {
  id: string;
  name: string;
  nbAvailableSeats?: number;
}

export interface SeatSelection {
  seatId?: number;
  node?: Konva.Group;
  data: CatalogPlanSeatResponseDto;
}
export interface SeatSelectionData {
  seatId: number;
  categoryId: number;
  priceId: number;
  isChecked?: boolean;
  isCollapsed?: boolean;
  seatRank?: string;
}
export interface CanvasProps {
  width: number;
  height: number;
  maxX: number;
  maxY: number;
  minX: number;
  minY: number;
}

export interface BlockTooltip {
  id: number;
  pointerPosition: Vector2d;
}

export interface SeatStatusOptionFilter {
  showReserved: boolean;
  showPaid: boolean;
  showEdited: boolean;
  showLocked: boolean;
  showControlled: boolean;
  showSubscribed: boolean;
}
export const defaultsSeatStatusOptionFilter: SeatStatusOptionFilter = {
  showReserved: true,
  showPaid: true,
  showEdited: true,
  showLocked: true,
  showControlled: true,
  showSubscribed: false,
};

export enum SeatColorsTypeEnum {
  Categories = 'Categories',
  Contingents = 'Contingents',
}

export interface SeatBlockModificationIds {
  categoryId: number | null;
  contingentId: number | null;
  unavailabilityId: number | null;
}
export interface GroupedSeatsByDoor {
  door: string;
  seats: CatalogPlanSeatResponseDto[];
}
export enum QuickFilterType {
  Contingent = 0,
  Category = 1,
  Unavailability = 2,
  Status = 3,
}
export interface MultiQuickFilterSelection {
  filterType: QuickFilterType | null;
  selectedContingents: EventSessionPlanContingentResponseDto[] | null;
  selectedCategories: EventSessionPlanCategoryResponseDto[] | null;
  selectedUnavailabilities: EventSessionPlanUnavailableSeatTypesResponseDto[] | null;
  selectedStatuses: SeatStatusFilterItem[] | null;
}

/**
 * Perimetre d'une selection en masse, **gele** au moment ou l'utilisateur clique sur « Selectionner
 * les places » (ou valide un filtre avec selection). Ce sont exactement les 6 parametres passes a
 * `getCatalogEventSessionMultiSelectionStats` pour produire les `bulkSelectionStats` affiches.
 *
 * Pourquoi le geler : la selection est **globale** et ne doit plus bouger quand l'utilisateur navigue
 * d'un bloc a l'autre. Le compteur (« Liberer les 153 place(s) indisponibles ») reste donc ancre sur
 * la selection, et surtout la modification de masse doit porter sur CES places, pas sur celles du
 * bloc ou l'utilisateur se trouve au moment de valider — sinon l'IHM annonce 153 places et l'API en
 * modifie un tout autre ensemble.
 */
export interface BulkSelectionScope {
  eventSessionId: number;
  contingentIds: number[];
  categoryIds: number[];
  unavailabilityIds: number[];
  blockId: number | null;
  seatStatusIds: string[];
}

export type EventSessionPlanResponseDtoTypes =
  | EventSessionPlanContingentResponseDto
  | EventSessionPlanCategoryResponseDto
  | EventSessionPlanUnavailableSeatTypesResponseDto;

/** Un item de liste du filtre rapide, quel que soit l'axe (les 3 DTO de plan + le statut). */
export type QuickFilterItem = EventSessionPlanResponseDtoTypes | SeatStatusFilterItem;

/**
 * Selection d'UN axe du filtre rapide, sous forme d'union discriminee par `type` : un `switch` sur
 * `type` narrow `items`, ce qui rend un appariement incoherent (ex. type Contingent + items de
 * statuts) impossible a la compilation.
 */
export type QuickFilterAxisPayload =
  | { type: QuickFilterType.Contingent; items: EventSessionPlanContingentResponseDto[] }
  | { type: QuickFilterType.Category; items: EventSessionPlanCategoryResponseDto[] }
  | { type: QuickFilterType.Unavailability; items: EventSessionPlanUnavailableSeatTypesResponseDto[] }
  | { type: QuickFilterType.Status; items: SeatStatusFilterItem[] };
export interface CatalogPlanDetailsTooltip {
  id: number;
  pointerPosition: Vector2d;
  type?: 'seat' | 'block';
}
export interface SeatStatusCounts {
  reserved: number;
  paid: number;
  edited: number;
  unavailable: number;
  free: number;
}
export interface CatalogPlanEventsDialogResult {
  selectedEvents: EventSessionForFilterResponseDto[];
  eventSessionIds: number[];
  cancelled?: boolean;
}
